import { InferOutput, any, array, boolean, literal, number, object, optional, string, union } from 'valibot';
import { ConfigurationProductSchema, FcisWidgetParametersSchema, StockProductSchema } from './PriceSchema';

export const SchemaAny = any();

const ProductTypeSchema = union([literal('CONFIGURATION'), literal('STOCK')]);

const IdentificationSchema = object({
  configurationId: optional(string()),
  commissionNumber: optional(string()),
  productCode: optional(string()),
  vehicleId: optional(string()),
  marketId: optional(string()),
  financialCode: optional(string()),
});

export const CommonSchema = object({
  common: object({
    hasCriticalError: boolean(),
    identification: IdentificationSchema,
    productType: ProductTypeSchema,
    isVan: boolean(),
    bm6: optional(string()),
    modelIdentifier: optional(string()),
    bodyType: optional(string()),
    brand: optional(string()),
    vehicleName: optional(string()),
  }),
});

export type Common = InferOutput<typeof CommonSchema>;

const BadgeSchema = object({
  name: string(),
  description: optional(string()),
  footnote: optional(string()),
});

export type Badge = InferOutput<typeof BadgeSchema>;

const BadgeGroupSchema = object({
  showIcon: boolean(),
  badges: array(BadgeSchema),
});

const TagSchema = optional(
  object({
    tag: object({
      badges: optional(array(BadgeGroupSchema)),
    }),
  }),
);

export type Tag = InferOutput<typeof TagSchema>;

const ImageSchema = optional(
  object({
    url: optional(string()),
    alt: optional(string()),
    title: optional(string()),
  }),
);

const ImageVehicleTileDataSchema = object({
  image: object({
    url: optional(string()),
    alt: optional(string()),
    title: optional(string()),
  }),
});

export type Image = InferOutput<typeof ImageSchema>;
export type ImageVehicleTileData = InferOutput<typeof ImageVehicleTileDataSchema>;

const HeadingSchema = optional(
  object({
    heading: object({
      content: optional(string()),
    }),
  }),
);

export type Heading = InferOutput<typeof HeadingSchema>;

const DescriptionSchema = optional(
  object({
    description: object({ content: optional(string()) }),
  }),
);

export type Description = InferOutput<typeof DescriptionSchema>;

const InfoBoxSchema = object({
  icon: optional(string()),
  content: optional(string()),
  color: optional(string()),
});

export type InfoBox = InferOutput<typeof InfoBoxSchema>;

const CharacteristicSchema = object({
  id: optional(string()),
  icon: optional(string()),
  value: optional(string()),
  footnotes: optional(array(string())),
});

const CharacteristicsVehicleTileDataSchema = optional(
  object({
    characteristics: array(CharacteristicSchema),
  }),
);

export type Characteristics = InferOutput<typeof CharacteristicSchema>;
export type CharacteristicsVehicleTileData = InferOutput<typeof CharacteristicsVehicleTileDataSchema>;

const MatchRateSchema = optional(
  object({
    rate: optional(number()),
  }),
);

export type MatchRate = InferOutput<typeof MatchRateSchema>;

const AvailabilityStatusSchema = union([literal('IN_STOCK'), literal('COMING_SOON'), literal('UPON_REQUEST')]);

const AvailabilitySchema = optional(
  object({
    status: optional(AvailabilityStatusSchema),
    estimatedArrivalDate: optional(string()),
  }),
);

export type Availability = InferOutput<typeof AvailabilitySchema>;

const ConsumptionSchema = object({
  id: optional(string()),
  label: optional(string()),
  value: optional(string()),
  footnotes: optional(array(string())),
  isEnergyEfficiency: optional(boolean()),
});

const ConsumptionsSchema = optional(
  object({
    consumption: array(ConsumptionSchema),
  }),
);

export type Consumption = InferOutput<typeof ConsumptionSchema>;
export type Consumptions = InferOutput<typeof ConsumptionsSchema>;

const HighlightSchema = object({
  content: optional(string()),
});

const HighlightsSchema = optional(
  object({
    highlights: array(HighlightSchema),
  }),
);

export type Highlight = InferOutput<typeof HighlightSchema>;
export type Highlights = InferOutput<typeof HighlightsSchema>;

const PricesSchema = optional(
  object({
    prices: object({
      price: optional(string()),
      plsProduct: optional(union([StockProductSchema, ConfigurationProductSchema])),
      fcisWidgetParameters: optional(FcisWidgetParametersSchema),
    }),
  }),
);

export type Prices = InferOutput<typeof PricesSchema>;

const EcoRatingVehicleTileDataSchema = optional(
  object({
    ecoRating: object({
      image: optional(ImageSchema),
    }),
  }),
);

const EcoRatingSchema = optional(
  object({
    image: optional(ImageSchema),
    emission: optional(number()),
  }),
);

export type EcoRating = InferOutput<typeof EcoRatingSchema>;
export type EcoRatingVehicleTileData = InferOutput<typeof EcoRatingVehicleTileDataSchema>;

const OnlineCodeSchema = optional(
  object({
    content: optional(string()),
  }),
);

export type OnlineCode = InferOutput<typeof OnlineCodeSchema>;

const CtaSchema = optional(
  object({
    cta: object({
      pdp: optional(string()),
      srp: optional(string()),
      label: optional(string()),
    }),
  }),
);

export type Cta = InferOutput<typeof CtaSchema>;

// delete if not necessary
export const VehicleTileDataSchema = object({
  common: CommonSchema,
  tag: TagSchema,
  image: ImageSchema,
  heading: HeadingSchema,
  description: DescriptionSchema,
  characteristics: array(CharacteristicSchema),
  prices: PricesSchema,
  highlights: array(HighlightSchema),
  consumption: array(ConsumptionSchema),
  ecoRating: EcoRatingSchema,
  cta: CtaSchema,
  onlineCode: OnlineCodeSchema,
  availability: AvailabilitySchema,
  matchRate: MatchRateSchema,
});

export type VehicleTileData = InferOutput<typeof VehicleTileDataSchema>;
